import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';

export interface ChoosableColumn {
  key: string;
  header: string;
}

const storageName = (screen: string) => `212ez.columns.${screen}`;

function stored(screen: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(storageName(screen));
    const value: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(value) && value.every((v) => typeof v === 'string') ? value : null;
  } catch {
    // Private windows and blocked storage: the default columns show.
    return null;
  }
}

/**
 * Which columns of a wide list this browser shows. The choice is kept in the browser, per
 * screen; it is a viewing preference, not account data. `initial` is what shows before
 * anyone has chosen; keys that no longer exist are ignored.
 */
export function useColumnChoice(
  screen: string,
  columns: readonly ChoosableColumn[],
  initial?: readonly string[],
) {
  const [chosen, setChosen] = useState<string[] | null>(() => stored(screen));
  const shown = useMemo(() => {
    const known = new Set(columns.map((c) => c.key));
    const keys = (chosen ?? initial ?? [...known]).filter((k) => known.has(k));
    return new Set(keys);
  }, [chosen, initial, columns]);
  const save = (keys: string[]) => {
    setChosen(keys);
    try {
      window.localStorage.setItem(storageName(screen), JSON.stringify(keys));
    } catch {
      // The choice then lasts until the page is left.
    }
  };
  return { shown, save };
}

interface Props {
  /** Dialog title, as the reference names it on that screen. */
  title: string;
  columns: readonly ChoosableColumn[];
  shown: ReadonlySet<string>;
  onSave: (keys: string[]) => void;
}

/** "Columns" button with the reference's column chooser: search, Select All, Save changes. */
export function ColumnChooser({ title, columns, shown, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const start = () => {
    setDraft(new Set(shown));
    setSearch('');
    setOpen(true);
  };
  const toggle = (key: string) =>
    setDraft((d) => {
      const next = new Set(d);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const needle = search.trim().toLowerCase();
  const listed = columns.filter((c) => c.header.toLowerCase().includes(needle));
  const all = columns.every((c) => draft.has(c.key));

  return (
    <>
      <Button variant="outlined" startIcon={<ViewColumnIcon />} onClick={start}>
        Columns
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Search for a column..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            autoFocus
          />
          <FormControlLabel
            sx={{ mt: 0.5, display: 'flex' }}
            control={
              <Checkbox
                checked={all}
                indeterminate={!all && draft.size > 0}
                onChange={() => setDraft(all ? new Set() : new Set(columns.map((c) => c.key)))}
              />
            }
            label="Select All"
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 2,
            }}
          >
            {listed.map((c) => (
              <FormControlLabel
                key={c.key}
                control={
                  <Checkbox
                    size="small"
                    checked={draft.has(c.key)}
                    onChange={() => toggle(c.key)}
                  />
                }
                label={c.header}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={draft.size === 0}
            onClick={() => {
              // Kept in list order, whatever order the boxes were ticked in.
              onSave(columns.filter((c) => draft.has(c.key)).map((c) => c.key));
              setOpen(false);
            }}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
